import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { useStringsContext } from '../../../../contexts/strings';
import MultipleSelectWithValidate from '../../../common/input/multiple-select-with-validate';
import { SelectOptions } from '../../../common/input/select-with-validate';
import svStrings from '../../../../sv-strings';
import { Option } from '../../../../types';
import { InputWithValidateProps } from '../../../common/input/text-input-with-validate';
import { useStaff } from '../../staff/hooks/staff';

const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;

const staffMultipleSelection = yup
  .array(yup.string())
  .min(1, svStrings.validations.mustSelectAlternative)
  .required(svStrings.validations.mustSelectAlternative);

const coachOptionsValidationKey = 'coachOptions';

interface Props extends Omit<InputWithValidateProps, 'onValue'> {
  selectedAbbrv: string;
  checkboxes?: boolean;
  onSelectedStaff: (selectedStaff: SelectOptions | null) => void;
  initialSelectedStaff?: SelectOptions;
}

const PollSelectedStaffClub = ({
  selectedAbbrv,
  initialSelectedStaff,
  onValidation,
  hideValidationError,
  checkboxes,
  onSelectedStaff,
}: Props) => {
  const { strings } = useStringsContext();
  const { fetchStaff } = useStaff();
  const initSelectedStaff = useRef(false);

  const [staffLoading, setStaffLoading] = useState(false);
  const [staffOptions, setStaffOptions] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });
  const [selectedStaff, setSelectedStaff] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });

  useEffect(() => {
    if (initialSelectedStaff && !initSelectedStaff.current) {
      setSelectedStaff(initialSelectedStaff);
      onSelectedStaff(initialSelectedStaff);
      initSelectedStaff.current = true;
    }
  }, [initialSelectedStaff, onSelectedStaff]);

  useEffect(() => {
    onValidation?.(coachOptionsValidationKey, selectedStaff.options.length > 0);
  }, [onValidation, selectedStaff.options.length]);

  useEffect(() => {
    const getStaff = async () => {
      if (!staffOptions.options.length && selectedAbbrv) {
        setStaffLoading(true);
        try {
          const staff = await fetchStaff(selectedAbbrv, String(REACT_APP_DEFAULT_LEAGUE_SEASON));
          if (staff) {
            const selectableStaff = staff.filter((member) => member.clId);
            setStaffOptions({
              options: selectableStaff.map(
                (member) =>
                  `${selectedAbbrv}/${String(REACT_APP_DEFAULT_LEAGUE_SEASON)}/${String(member.clId)}`
              ),
              optionsText: selectableStaff.map(
                (member) => member.fullName || member.displayName || String(member.clId)
              ),
            });
          }
        } catch (error) {
          // Do nothing
        }
        setStaffLoading(false);
      }
    };
    getStaff();
  }, [fetchStaff, selectedAbbrv, staffOptions.options]);

  const handleStaffSelectedValue = useCallback(
    (value: Option[]) => {
      const optionsText = value?.map((option) => {
        const index = staffOptions.options.findIndex((o) => o === option);
        return staffOptions.optionsText[index];
      });

      const updatedSelectedStaff = {
        options: value,
        optionsText,
      };

      setSelectedStaff(updatedSelectedStaff);
      onSelectedStaff(updatedSelectedStaff);
    },
    [onSelectedStaff, staffOptions.options, staffOptions.optionsText]
  );

  return (
    <MultipleSelectWithValidate
      name={coachOptionsValidationKey}
      options={staffOptions.options}
      optionsText={staffOptions.optionsText}
      validation={staffMultipleSelection}
      label={strings.polls.selectCoachesLabel}
      onValidation={onValidation}
      value={selectedStaff.options || []}
      onValue={(_, value) => handleStaffSelectedValue(value)}
      hideValidationError={hideValidationError}
      disabled={staffLoading}
      checkboxes={checkboxes}
    />
  );
};

export default PollSelectedStaffClub;
